/**
 * Returns: the queue of buyer requests waiting for a decision, and the
 * history of every return, filtered.
 *
 * Staff with manage_returns approve or reject requests, one or many at a
 * time. A buyer login sees its own returns here, and asks for one from a
 * lead's page.
 */
import { useMemo, useState } from 'react';
import {
  Alert, Anchor, Badge, Button, Checkbox, Group, Loader, Modal, Select, Stack, Table, Tabs, Text, Textarea, TextInput,
} from '@mantine/core';
import { notifications } from '@mantine/notifications';
import { useSearchParams } from 'react-router-dom';
import { useCampaigns, useEntities } from '../../api/queries';
import { useDecideReturns, useReturns, useReturnsSettings, type ReturnsQuery } from '../../api/returns';
import { useNavUser } from '../../layout/navUser';
import { isBuyerLogin, seesReturns, hasAction, isScopedUser } from '../../layout/nav';
import { PageHeader } from '../../ui';
import { formatGBP } from '../../theme';
import { useVocab } from '../../vocab';
import { LeadDetailModal } from '../LeadDetailModal';
import { ReasonCell, ReturnStatusBadge, WhoCell, returnAmount } from './returnsShared';

type Filters = Pick<ReturnsQuery, 'status' | 'buyer_id' | 'campaign_id' | 'supplier_id' | 'reason_code' | 'from' | 'to'>;

export function ReturnsPage() {
  const v = useVocab();
  const user = useNavUser();
  const staff = !isScopedUser(user) && hasAction(user, 'manage_returns');
  const buyer = isBuyerLogin(user);
  const [params, setParams] = useSearchParams();
  const tab = params.get('tab') === 'history' || !staff ? 'history' : 'queue';
  const [openLead, setOpenLead] = useState<string | null>(null);

  if (!seesReturns(user)) {
    return <Alert color="gray" variant="light">Returns are not available to this login.</Alert>;
  }

  return (
    <Stack gap="lg">
      <PageHeader
        title="Returns"
        description={
          buyer
            ? `${v.Many('lead')} you have returned or asked to return. Ask from a ${v.one('lead')}'s page, inside your return window.`
            : `${v.One('buyer')} returns: requests waiting for a decision, and every return made.`
        }
      />
      {staff ? (
        <Tabs
          value={tab}
          onChange={(t) => setParams((p) => { p.set('tab', t ?? 'queue'); return p; }, { replace: true })}
        >
          <Tabs.List mb="md">
            <Tabs.Tab value="queue">Waiting</Tabs.Tab>
            <Tabs.Tab value="history">History</Tabs.Tab>
          </Tabs.List>
          <Tabs.Panel value="queue"><Queue onOpen={setOpenLead} /></Tabs.Panel>
          <Tabs.Panel value="history"><History staff onOpen={setOpenLead} /></Tabs.Panel>
        </Tabs>
      ) : (
        <History staff={false} onOpen={setOpenLead} />
      )}
      <LeadDetailModal id={openLead} onClose={() => setOpenLead(null)} />
    </Stack>
  );
}

function LeadLink({ id, onOpen }: { id: string; onOpen: (id: string) => void }) {
  return (
    <Anchor component="button" type="button" size="sm" ff="monospace" onClick={() => onOpen(id)}>
      {id.slice(0, 8)}
    </Anchor>
  );
}

function Queue({ onOpen }: { onOpen: (id: string) => void }) {
  const v = useVocab();
  const { data, isPending, error } = useReturns({ status: 'requested', limit: 500 });
  const decide = useDecideReturns();
  const [checked, setChecked] = useState<Set<string>>(new Set());
  const [deciding, setDeciding] = useState<null | 'approve' | 'reject'>(null);
  const [note, setNote] = useState('');
  const rows = data?.returns ?? [];
  const ids = rows.map((r) => r.id);
  const allOn = ids.length > 0 && ids.every((id) => checked.has(id));
  const chosen = rows.filter((r) => checked.has(r.id));
  const total = chosen.reduce((s, r) => s + r.sale_amount, 0);

  async function go() {
    if (!deciding) return;
    try {
      const r = await decide.mutateAsync({ ids: [...checked], decision: deciding, note: note.trim() || null });
      notifications.show({
        color: r.failed.length ? 'orange' : 'teal',
        title: deciding === 'approve' ? `${r.returns.length} approved` : `${r.returns.length} rejected`,
        message: r.failed.length
          ? `${r.failed.length} could not be: ${r.failed[0]!.error}`
          : deciding === 'approve'
            ? `${formatGBP(r.returns.reduce((s, x) => s + x.credit_amount, 0))} credited.`
            : `The ${v.many('buyer')} will see why.`,
      });
      setChecked(new Set());
      setDeciding(null);
      setNote('');
    } catch (e) {
      notifications.show({ color: 'red', title: 'Not decided', message: (e as Error).message });
    }
  }

  if (isPending) return <Group justify="center" p="xl"><Loader /></Group>;
  if (error) return <Alert color="red" variant="light">{error.message}</Alert>;
  return (
    <Stack>
      <Group justify="space-between">
        <Text size="sm" c="dimmed">
          {rows.length === 0 ? 'Nothing waiting.' : `${rows.length} waiting${checked.size ? `, ${checked.size} chosen (${formatGBP(total)})` : ''}`}
        </Text>
        <Group gap="sm">
          <Button variant="default" disabled={checked.size === 0} onClick={() => setDeciding('reject')}>Reject</Button>
          <Button disabled={checked.size === 0} onClick={() => setDeciding('approve')}>Approve</Button>
        </Group>
      </Group>
      {rows.length > 0 && (
        <Table.ScrollContainer minWidth={900}>
          <Table striped verticalSpacing="sm">
            <Table.Thead>
              <Table.Tr>
                <Table.Th w={36}>
                  <Checkbox
                    aria-label="Choose every request"
                    checked={allOn}
                    indeterminate={checked.size > 0 && !allOn}
                    onChange={() => setChecked(allOn ? new Set() : new Set(ids))}
                  />
                </Table.Th>
                <Table.Th>{v.One('lead')}</Table.Th>
                <Table.Th>{v.One('buyer')}</Table.Th>
                <Table.Th>{v.One('campaign')}</Table.Th>
                <Table.Th>{v.One('supplier')}</Table.Th>
                <Table.Th>Reason</Table.Th>
                <Table.Th>Paid</Table.Th>
                <Table.Th>Asked</Table.Th>
              </Table.Tr>
            </Table.Thead>
            <Table.Tbody>
              {rows.map((r) => (
                <Table.Tr key={r.id}>
                  <Table.Td>
                    <Checkbox
                      aria-label={`Choose the return of ${r.lead_id}`}
                      checked={checked.has(r.id)}
                      onChange={() => setChecked((c) => {
                        const n = new Set(c);
                        if (n.has(r.id)) n.delete(r.id); else n.add(r.id);
                        return n;
                      })}
                    />
                  </Table.Td>
                  <Table.Td><LeadLink id={r.lead_id} onOpen={onOpen} /></Table.Td>
                  <Table.Td><Text size="sm">{r.buyer_name}</Text></Table.Td>
                  <Table.Td><Text size="sm">{r.campaign_name || r.campaign_id}</Text></Table.Td>
                  <Table.Td><Text size="sm">{r.supplier_name || r.supplier_id || '(none)'}</Text></Table.Td>
                  <Table.Td><ReasonCell r={r} /></Table.Td>
                  <Table.Td><Text size="sm" ff="monospace">{formatGBP(r.sale_amount)}</Text></Table.Td>
                  <Table.Td><WhoCell r={r} /></Table.Td>
                </Table.Tr>
              ))}
            </Table.Tbody>
          </Table>
        </Table.ScrollContainer>
      )}
      <Modal
        opened={deciding !== null}
        onClose={() => setDeciding(null)}
        title={deciding === 'approve' ? `Approve ${checked.size === 1 ? 'this return' : `${checked.size} returns`}?` : `Reject ${checked.size === 1 ? 'this return' : `${checked.size} returns`}?`}
      >
        <Stack>
          <Text size="sm">
            {deciding === 'approve'
              ? `${formatGBP(total)} will be credited and taken off the ${v.many('lead')}' revenue. Caps used this period are given back, and ${v.many('supplier')} are charged back unless switched off.`
              : `Nothing changes on the ${v.many('lead')}. The ${v.many('buyer')} see the note.`}
          </Text>
          <Textarea
            label="Note"
            description={`Shown to the ${v.one('buyer')}.`}
            value={note}
            onChange={(e) => setNote(e.currentTarget.value)}
            autosize
            minRows={2}
          />
          <Group justify="flex-end">
            <Button variant="subtle" onClick={() => setDeciding(null)}>Cancel</Button>
            <Button color={deciding === 'approve' ? undefined : 'red'} loading={decide.isPending} onClick={go}>
              {deciding === 'approve' ? 'Approve' : 'Reject'}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </Stack>
  );
}

function History({ staff, onOpen }: { staff: boolean; onOpen: (id: string) => void }) {
  const v = useVocab();
  const [f, setF] = useState<Filters>({});
  const query = useMemo(() => ({ ...f, limit: 200 }), [f]);
  const { data, isPending, error, isPlaceholderData } = useReturns(query);
  const settings = useReturnsSettings();
  const buyers = useEntities('buyers', staff);
  const suppliers = useEntities('suppliers', staff);
  const campaigns = useCampaigns(staff);
  const set = (k: keyof Filters) => (value: string | null) => setF((x) => ({ ...x, [k]: value || undefined }));
  const credited = (data?.returns ?? []).filter((r) => r.status === 'approved').reduce((s, r) => s + r.credit_amount, 0);

  return (
    <Stack>
      <Group gap="sm" align="flex-end" wrap="wrap">
        <Select
          label="Status"
          placeholder="Any"
          clearable
          w={150}
          data={[
            { value: 'requested', label: 'Requested' },
            { value: 'approved', label: 'Approved' },
            { value: 'rejected', label: 'Rejected' },
          ]}
          value={f.status ?? null}
          onChange={set('status')}
        />
        {staff && (
          <>
            <Select
              label={v.One('buyer')} placeholder="Any" clearable searchable w={180}
              data={(buyers.data?.entities ?? []).map((b) => ({ value: b.id, label: b.name }))}
              value={f.buyer_id ?? null} onChange={set('buyer_id')}
            />
            <Select
              label={v.One('campaign')} placeholder="Any" clearable searchable w={180}
              data={(campaigns.data?.campaigns ?? []).map((c) => ({ value: c.id, label: c.name }))}
              value={f.campaign_id ?? null} onChange={set('campaign_id')}
            />
            <Select
              label={v.One('supplier')} placeholder="Any" clearable searchable w={180}
              data={(suppliers.data?.entities ?? []).map((s) => ({ value: s.id, label: s.name }))}
              value={f.supplier_id ?? null} onChange={set('supplier_id')}
            />
          </>
        )}
        <Select
          label="Reason" placeholder="Any" clearable w={200}
          data={(settings.data?.reasons ?? []).map((r) => ({ value: r.code, label: r.label }))}
          value={f.reason_code ?? null} onChange={set('reason_code')}
        />
        <TextInput label="From" type="date" w={150} value={f.from ?? ''} onChange={(e) => set('from')(e.currentTarget.value)} />
        <TextInput label="To" type="date" w={150} value={f.to ?? ''} onChange={(e) => set('to')(e.currentTarget.value)} />
      </Group>
      {error && <Alert color="red" variant="light">{error.message}</Alert>}
      {isPending && <Group justify="center" p="xl"><Loader /></Group>}
      {data && (
        <div className={isPlaceholderData ? 'hf-stale' : 'hf-fresh'}>
          <Group justify="space-between" mb="xs">
            <Text size="sm" c="dimmed">
              {`${data.total} ${data.total === 1 ? 'return' : 'returns'}${data.returns.length < data.total ? `, the newest ${data.returns.length} shown` : ''}`}
            </Text>
            <Group gap="xs">
              <Badge variant="light" color="blue">{`${data.counts.requested} waiting`}</Badge>
              <Badge variant="light" color="teal">{`${formatGBP(credited)} credited here`}</Badge>
            </Group>
          </Group>
          {data.returns.length === 0 ? (
            <Text size="sm" c="dimmed" ta="center" py="lg">No returns match.</Text>
          ) : (
            <Table.ScrollContainer minWidth={staff ? 980 : 720}>
              <Table striped verticalSpacing="sm">
                <Table.Thead>
                  <Table.Tr>
                    <Table.Th>{v.One('lead')}</Table.Th>
                    {staff && <Table.Th>{v.One('buyer')}</Table.Th>}
                    <Table.Th>{v.One('campaign')}</Table.Th>
                    {staff && <Table.Th>{v.One('supplier')}</Table.Th>}
                    <Table.Th>Reason</Table.Th>
                    <Table.Th>Amount</Table.Th>
                    <Table.Th>Status</Table.Th>
                    <Table.Th>History</Table.Th>
                  </Table.Tr>
                </Table.Thead>
                <Table.Tbody>
                  {data.returns.map((r) => (
                    <Table.Tr key={r.id}>
                      <Table.Td><LeadLink id={r.lead_id} onOpen={onOpen} /></Table.Td>
                      {staff && <Table.Td><Text size="sm">{r.buyer_name}</Text></Table.Td>}
                      <Table.Td><Text size="sm">{r.campaign_name || r.campaign_id}</Text></Table.Td>
                      {staff && <Table.Td><Text size="sm">{r.supplier_name || r.supplier_id || '(none)'}</Text></Table.Td>}
                      <Table.Td><ReasonCell r={r} /></Table.Td>
                      <Table.Td><Text size="sm" ff="monospace">{returnAmount(r)}</Text></Table.Td>
                      <Table.Td><ReturnStatusBadge status={r.status} /></Table.Td>
                      <Table.Td><WhoCell r={r} /></Table.Td>
                    </Table.Tr>
                  ))}
                </Table.Tbody>
              </Table>
            </Table.ScrollContainer>
          )}
        </div>
      )}
    </Stack>
  );
}
